import React, { useEffect, useState, useContext } from 'react';

import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import {
  useIntl,
  I18nContext,
} from '../../plugins/publicis-gatsby-plugin-i18n';
import DefaultShareImage from '../assets/images/default-share-image.jpg';
import AccessibilityNav from '../components/AccessibilityNav/AccessibilityNav';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import PopinDiscoverStore from '../components/PopinDiscoverStore/PopinDiscoverStore';

import './style.scss';

const Layout = ({ children, noFollow, eventBannerData, imagesArray = [] }) => {
  const location = useLocation();
  const intl = useIntl();
  const locale = intl.locale;
  const i18n = useContext(I18nContext);
  const [alternatePath] = useState(
    i18n.paths.filter((a) => a.label != locale)
  );
  let code_country = "";
  switch (locale) {
    case "fr":
      code_country = "-FR"
      break;
    case "en":
      code_country = "-US"
      break;
    case "de":
      code_country = "-DE"
      break;
    default:
      code_country = ""
  }
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('scroll-down', 'scroll-up');

    const onKeyUp = ({ key }) => {
      if (key === 'Escape' || key === 27) {
        document.body.classList.remove('show_accessibility_popin');
        document.body.classList.remove('show_popin_stores', 'popin_open');
      }
      if (
        (key === 'Enter' || key === 13) &&
        document.activeElement.classList.contains('dropdown-header')
      ) {
        document.activeElement.click();
      }
    };

    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const data = useStaticQuery(graphql`
    query layout {
      site {
        siteMetadata {
          title
          siteUrl
        }
      }
      allMenuLinkContentMenuLinkContent(
        filter: { enabled: {eq: true}}
        sort: { order: ASC, fields: weight }
      ) {
        edges {
          node {
            id
            weight
            enabled
            title
            expanded
            menu_name
            langcode
            field_hidden
            drupal_parent_menu_item
            drupal_id
            drupal_internal__id
            link {
              uri
              url
            }
          }
        }
      }
      socialMedia: allMenuLinkContentMenuLinkContent(
        filter: { menu_name: { eq: "social-media" } }
      ) {
        nodes {
          menu_name
          title
          link {
            title
            uri
            url
          }
        }
      }
      allMenuMenu(
        filter: { id: { eq: "285193f1-5a92-5244-a824-9de08db095de" } }
      ) {
        edges {
          node {
            label
          }
        }
      }
      latestPublicationFr: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "fr" }
          relationships: { field_category: { drupal_internal__tid: { eq: 8 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 2
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
              field_media_pdf {
                drupal_internal__mid
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }
      latestPublicationDe: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "de" }
          relationships: { field_category: { drupal_internal__tid: { eq: 8 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 2
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
              field_media_pdf {
                drupal_internal__mid
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }
      latestPublicationEn: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "en" }
          relationships: { field_category: { drupal_internal__tid: { eq: 8 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 1
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
              field_media_pdf {
                drupal_internal__mid
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }
      latestCommunicationFr: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "fr" }
          relationships: { field_category: { drupal_internal__tid: { eq: 7 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 2
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }
      latestCommunicationDe: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "de" }
          relationships: { field_category: { drupal_internal__tid: { eq: 7 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 2
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }
      latestCommunicationEn: allNodeArticle(
        filter: {
          status: { eq: true }
          langcode: { eq: "en" }
          relationships: { field_category: { drupal_internal__tid: { eq: 7 } } }
        }
        sort: { fields: created, order: DESC }
        limit: 2
      ) {
        edges {
          node {
            langcode
            title
            promote
            relationships {
              field_category {
                drupal_internal__tid
                name
              }
            }
            path {
              alias
              langcode
            }
          }
        }
      }

      allDocuments: allMediaDocument(
        filter: { langcode: { eq: "fr" } }
        limit: 3000
      ) {
        edges {
          node {
            langcode
            field_media_document {
              description
            }
            relationships {
              field_media_document {
                filesize
                filename
                filemime
                uri {
                  url
                }
              }
            }
            drupal_internal__mid
          }
        }
      }
      allFile(filter: { extension: { eq: "css" }, name: { eq: "print" } }) {
        nodes {
          name
          publicURL
        }
      }
    }
  `);

  const print_css_url = data.allFile.nodes[0]?.publicURL;
  const childrenWithProps = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      id: 'page_template',
      // anything you want to pass down can go here
    })
  );
  return (
    <>
      <Helmet htmlAttributes={{
        lang: locale + code_country,
      }}>
        <script>
          {` (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push(

          {'gtm.start': new Date().getTime(),event:'gtm.js'}
          );var f=d.getElementsByTagName(s)[0],

          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=

          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);

          })(window,document,'script','dataLayer','` +
            process.env.GATSBY_GOOGLE_ANALYTICS_ID +
            '\');'}
        </script>
        <script>
          {`
              if(!dataLayer){
                var dataLayer = [];
              }
              dataLayer.push({
                langueCB: "${locale}",
                event: "cookie_consent_update",
                gtm: {uniqueEventId: 9},
                developer_id: {dMWZhNz: true},
                ads_data_redaction: false
              })
            `}
        </script>
        <link
          rel="stylesheet"
          type="text/css"
          href={print_css_url}
          media="print"
        />
        <link rel="canonical" href={location.href} />
        <link
          rel="alternate"
          hrefLang={alternatePath[0]?.label}
          href={data.site?.siteMetadata?.siteUrl + alternatePath[0]?.link}
        />

        {/* {
          process.env.GOOGLE_SITE_VERIFICATION &&
          <meta
            name="google-site-verification"
            content={process.env.GOOGLE_SITE_VERIFICATION}
          />
        } */}

        {noFollow ? <meta name="robots" content='noindex,nofollow' /> : null}

        <noscript>
          {
            `
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=${process.env.GATSBY_GOOGLE_ANALYTICS_ID}"
              height="0"
              width="0"
              style="display: none; visibility: hidden;"
            ></iframe>
            `
          }
        </noscript>
      </Helmet>

      <img
        className="sharedefaultImage"
        src={data.site?.siteMetadata?.siteUrl + DefaultShareImage}
        alt=""
        style={{ display: 'none' }}
      />
      <AccessibilityNav />
      <Header
        siteTitle={data.site?.siteMetadata?.title || 'Title'}
        pathname={location?.pathname}
        eventBannerData={eventBannerData}
        imagesArray={imagesArray}
        mainMenu={data.allMenuLinkContentMenuLinkContent?.edges?.filter(
          ({ node }) => node?.langcode === locale
        )}
        latestPublication={
          locale === 'fr'
            ? data.latestPublicationFr?.edges[0]?.node
            : locale === 'en' ? data.latestPublicationEn?.edges[0]?.node : data.latestPublicationDe?.edges[0]?.node
        }
        latestCommunication={
          locale === 'fr'
            ? data.latestCommunicationFr?.edges[0]?.node
            :  locale === 'en' ? data.latestCommunicationEn?.edges[0]?.node : data.latestCommunicationDe?.edges[0]?.node
        }
        allDocuments={data.allDocuments?.edges}
      />
      <main className="body-main" id="pagecontent" role="main">
        {childrenWithProps}
      </main>
      <PopinDiscoverStore />
      <Footer
        footerContactMenu={data.allMenuLinkContentMenuLinkContent?.edges.filter(
          ({ node }) => node?.langcode === locale
        )}
        socialMedia={data.socialMedia}
      ></Footer>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  noFollow: PropTypes.bool
};

export default Layout;
